import { toast } from '@/components/toast';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import { useRemoveWatermarkAccess } from '@/hooks/useRemoveWatermarkAccess';
import { useAnalytics } from '@/services/analytics';
import { createTranslation } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { GET_TRANSLATION_GROUPS_KEY, uploadingTranslationGroupsAtom } from '@/state/translations';
import { ITranslationGroup, TranslationGroupStatus } from '@d-id/types';
import axios, { AxiosError } from 'axios';
import { nanoid } from 'nanoid';
import { useMutation, useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';

export enum ERROR_KINDS {
    CelebrityDetectedError = 'CelebrityDetectedError',
    FaceError = 'FaceError',
    TextModerationError = 'TextModerationError',
}

interface CreateTranslationProps {
    uploadFilePromise: Promise<string>;
    selectedLanguages: string[];
    file: File;
    thumbnail_url: string;
    mixpanelProps: Record<string, unknown>;
    proofreading?: boolean;
    options?: CreateTranslationMutationOptions;
    sourceLanguage: string | null;
}

interface CreateTranslationMutationOptions {
    skipRefetch?: boolean;
}

export const useCreateTranslation = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const setUploadingTranslations = useSetRecoilState(uploadingTranslationGroupsAtom);
    const mixpanel = useAnalytics();
    const { removeWatermarkSelected } = useRemoveWatermarkAccess();
    const { incrementUsage } = useFreeVideos();

    const createTranslationFn = async ({
        uploadFilePromise,
        selectedLanguages,
        mixpanelProps,
        proofreading,
        sourceLanguage,
    }: {
        uploadFilePromise: Promise<string>;
        selectedLanguages: string[];
        mixpanelProps: Record<string, unknown>;
        proofreading?: boolean;
        sourceLanguage: string | null;
    }) => {
        let fileUrl;
        try {
            fileUrl = await uploadFilePromise;
            mixpanel?.track('translate-video', {
                event: 'upload-done',
                ...mixpanelProps,
            });
        } catch (error) {
            mixpanel?.track('translate-video', {
                event: 'upload-fail',
                ...mixpanelProps,
            });
            throw error;
        }
        const trackEventName = proofreading ? 'translate-proofread' : 'translate-video';

        try {
            const createTranslationResult = await createTranslation({
                languages: selectedLanguages,
                sourceUrl: fileUrl,
                removeWatermark: removeWatermarkSelected,
                proofreading,
                sourceLanguage,
            });

            mixpanel?.track(trackEventName, {
                event: 'success',
                ...mixpanelProps,
            });
            await incrementUsage.mutateAsync(selectedLanguages.length);
            return createTranslationResult;
        } catch (error) {
            mixpanel?.track(trackEventName, {
                event: 'fail',
                ...mixpanelProps,
            });
            throw error;
        }
    };

    const mutation = useMutation(
        ({ uploadFilePromise, selectedLanguages, mixpanelProps, proofreading, sourceLanguage }: CreateTranslationProps) =>
            createTranslationFn({
                uploadFilePromise,
                selectedLanguages,
                mixpanelProps,
                proofreading,
                sourceLanguage,
            }),
        {
            onMutate: async ({ file, selectedLanguages, thumbnail_url, proofreading }: CreateTranslationProps) => {
                if (proofreading) {
                    return;
                }

                const previousData = queryClient.getQueryData(GET_TRANSLATION_GROUPS_KEY);

                const newGroup: ITranslationGroup = {
                    id: nanoid(),
                    name: `${file?.name.substring(0, file?.name.lastIndexOf('.')).replaceAll(' ', '-') || 'untitled_translation_group'}`,
                    status: TranslationGroupStatus.Uploading,
                    owner_id: user?.org_id || user?.sub || 'temp',
                    created_by: user?.sub || 'temp',
                    created_at: new Date().toISOString(),
                    modified_at: new Date().toISOString(),
                    thumbnail_url,
                    target_languages: selectedLanguages,
                    source_url: 'temp',
                    subtitles_url: 'temp',
                    persist: true,
                };

                setUploadingTranslations(currVal => {
                    return [...currVal, newGroup];
                });

                return { previousData, uploadedMockedId: newGroup.id };
            },
            onSuccess(data, __, context) {
                // put the real id on the mock uploaded translation, removing will result flickering
                setUploadingTranslations(currUploadingTranslations => {
                    return currUploadingTranslations.map(uploadingTranslation => {
                        if (uploadingTranslation.id === context?.uploadedMockedId) {
                            return { ...uploadingTranslation, id: data?.translations[0]?.group_id };
                        }
                        return uploadingTranslation;
                    });
                });
            },
            onError: (error, _, context) => {
                if (context?.previousData) {
                    queryClient.setQueryData(GET_TRANSLATION_GROUPS_KEY, context?.previousData);
                }

                if ((error as any)?.response?.status === 429) {
                    toast.error({ title: 'Too many requests, please try again later' });
                } else if (axios.isAxiosError(error)) {
                    const errorType = (error as AxiosError<{ kind: ERROR_KINDS }>)?.response?.data?.kind;
                    if (errorType === ERROR_KINDS.FaceError) {
                        toast.error({ title: 'No face detected in your uploaded footage', toastId: `create-translation-error` });
                        return;
                    }
                    if (errorType === ERROR_KINDS.CelebrityDetectedError) {
                        toast.error({
                            title: 'Famous figure detected',
                            toastId: `create-translation-error`,
                            message:
                                'The footage you’ve uploaded contains a famous figure, which is not according to our content guidelines.',
                        });
                        return;
                    }
                }
                toast.error({ title: 'Could not create translation videos', toastId: `create-translation-error` });
                logger.error('Could not create translation videos', error);
            },
            onSettled: (_, __, { options }) => {
                if (!options?.skipRefetch) {
                    queryClient.invalidateQueries(GET_TRANSLATION_GROUPS_KEY);
                }
            },
        }
    );

    return mutation;
};
